import { useEffect, useMemo, useState } from 'react';
import MixpanelConfig from '../constants/mixpanelConfig';
import { TradeStateName } from '../types/trade';
import { BidStatus, BidStatusName } from '../types/auction';
import TradeDetails from '../models/TradeDetails';
import useAnalytics, { getSellerProperties } from './useAnalytics';
import segmentConfig from '../constants/segmentConfig';
import getVehicleCardsViewType from '../helpers/getVehicleCardsViewType';

interface UseMpBidTracking {
  tradeDetails: any;
  trackBidFlag: boolean;
  trackProxyFlag: boolean;
  trackBidErrorFlag: boolean;
  trackHasBid: boolean;
  trackShowConfirmModal: boolean;
  mpBidError: string[];
  proxyAmount: number;
  numberOfBids: number;
  bidStatus: number;
  bidAmount: number;
  liveBid: any;
  tradeState: number;
  manufacturers: any;
  bidSource: string;
  bidMpEventName: string;
  bidType: string;
  setMpBidError: (array) => void;
  setTrackBidFlag: (boolean) => void;
  setTrackProxyFlag: (boolean) => void;
  setTrackBidErrorFlag: (boolean) => void;
  setTrackHasBid: (boolean) => void;
  setTrackShowConfirmModal: (boolean) => void;
  confirmationMpTrackers?: {
    confirmation: boolean;
    highBid: boolean;
    outbidDealerGroup: boolean;
    outbidDealership: boolean;
  };
  bidUsers?: object[];
  userId?: number;
  biddingDealerId?: number;
  isOmp?: boolean;
  ecsId?: string;
  organizationName?: string;
}

// This hook is used to track Mixpanel bid related events including
// Bid, Proxy bid and Bid error events
const useMpBidTracking = ({
  bidAmount,
  biddingDealerId,
  bidSource,
  bidStatus,
  bidType,
  bidUsers,
  confirmationMpTrackers,
  liveBid,
  manufacturers,
  mpBidError,
  numberOfBids,
  proxyAmount,
  setMpBidError,
  setTrackBidErrorFlag,
  setTrackBidFlag,
  setTrackHasBid,
  setTrackProxyFlag,
  setTrackShowConfirmModal,
  trackBidErrorFlag,
  trackBidFlag,
  trackHasBid,
  trackProxyFlag,
  trackShowConfirmModal,
  tradeDetails,
  tradeState,
  userId,
  isOmp,
  ecsId,
  organizationName
}: UseMpBidTracking) => {
  const track = useAnalytics();
  const [currentBids, setCurrentBids] = useState({
    prev: 0,
    current: tradeDetails?.auctionSummary?.maxBid
  });

  // Tracks the latest 2 bids because auctionSummary updates too quickly to be used reliable for current_top_bid
  useEffect(() => {
    if (
      tradeDetails?.auctionSummary?.maxBid &&
      tradeDetails?.auctionSummary?.maxBid > currentBids.current
    ) {
      setCurrentBids(bids => ({
        current: tradeDetails?.auctionSummary?.maxBid,
        prev: bids.current
      }));
    }
  }, [currentBids, tradeDetails?.auctionSummary?.maxBid]);

  const defaultTrackingParameters = useMemo(
    () => ({
      auction_id: tradeDetails?.auctionSummary?.id || null,
      asIs: tradeDetails?.details?.isAsIs ?? tradeDetails?.isAsIs,
      bid_type: bidType ? segmentConfig.analyticsBidType[bidType] : null,
      buyer_dealer_id: biddingDealerId?.toString(),
      buyer_source_system_type: segmentConfig.sourceSystemType.traderev,
      buyer_rep_source_system_id: userId?.toString(),
      buyer_org_ecs_id: ecsId,
      buyer_org_name_value: organizationName,
      is_timed_event: !!tradeDetails?.saleEventId,
      is_floor_met:
        (tradeDetails?.isFloorPriceMet ||
          tradeDetails?.details?.tradeFloorPrice?.isFloorPriceMet ||
          (liveBid?.hasFloorPrice && liveBid?.isFloorPriceMet)) ??
        false,
      is_close_to_floor:
        tradeDetails?.isCloseToFloorPrice ??
        tradeDetails?.details?.tradeFloorPrice?.isCloseToFloorPrice ??
        false,
      is_in_stock: !!tradeDetails?.isLanded,
      make_value: tradeDetails?.details?.tradeVehicleSummary?.vehicleSummary
        ? TradeDetails.getManufacturerName(
            tradeDetails?.details?.tradeVehicleSummary?.vehicleSummary,
            manufacturers
          )
        : tradeDetails?.make,
      model_value:
        tradeDetails?.details?.tradeVehicleSummary?.vehicleSummary?.model
          ?.displayName ??
        tradeDetails?.tradeVehicleSummary?.vehicleSummary?.model?.displayName,
      network_type: tradeDetails?.network?.displayName,
      proxy_amount_value: null,
      source: bidSource,
      trade_id: tradeDetails?.id,
      trade_state: TradeStateName[tradeState] ?? null,
      trim:
        tradeDetails?.trim ??
        tradeDetails?.details?.tradeVehicleSummary?.vehicleSummary?.trim,
      user_bid_status: bidStatus
        ? BidStatusName[bidStatus]
        : BidStatusName[BidStatus.none],
      vehicle_cards_view_type: getVehicleCardsViewType(),
      vin: tradeDetails?.vin ?? tradeDetails?.details?.vin,
      year:
        tradeDetails?.details?.tradeVehicleSummary?.vehicleSummary?.modelYear ??
        tradeDetails?.tradeVehicleSummary?.vehicleSummary?.modelYear
    }),
    [
      tradeDetails?.auctionSummary?.id,
      tradeDetails?.details?.isAsIs,
      tradeDetails?.details?.tradeVehicleSummary?.vehicleSummary,
      tradeDetails?.details?.tradeFloorPrice?.isCloseToFloorPrice,
      tradeDetails?.details?.tradeFloorPrice?.isFloorPriceMet,
      tradeDetails?.isCloseToFloorPrice,
      tradeDetails?.isFloorPriceMet,
      tradeDetails?.isLanded,
      tradeDetails?.saleEventId,
      tradeDetails?.details?.vin,
      tradeDetails?.isAsIs,
      tradeDetails?.make,
      tradeDetails?.tradeVehicleSummary?.vehicleSummary?.model?.displayName,
      tradeDetails?.tradeVehicleSummary?.vehicleSummary?.modelYear,
      tradeDetails?.network?.displayName,
      tradeDetails?.id,
      tradeDetails?.trim,
      tradeDetails?.vin,
      bidType,
      biddingDealerId,
      userId,
      ecsId,
      organizationName,
      manufacturers,
      bidSource,
      tradeState,
      bidStatus,
      liveBid?.hasFloorPrice,
      liveBid?.isFloorPriceMet
    ]
  );

  useEffect(() => {
    // Determines if this is the first bid on the auction
    function isFirstBid(_bidUsers, _userId) {
      if (!_bidUsers || _bidUsers?.length === 0) {
        return true;
      }

      // check if user has a bid registered in the bid users list
      return !_bidUsers.some(user => user.id === _userId);
    }

    // Track bid on mixpanel
    function trackBid(bid) {
      if (!tradeDetails || !track) {
        return;
      }

      track(
        isOmp
          ? MixpanelConfig.names.placeBidOmp
          : MixpanelConfig.names.placeBid,
        {
          ...defaultTrackingParameters,
          ...getSellerProperties(tradeDetails),
          bid_value: bid || liveBid?.maxBidAmount,
          confirm_bid_modal: confirmationMpTrackers?.confirmation,
          current_top_bid:
            bidAmount === currentBids.current
              ? currentBids.prev
              : tradeDetails?.auctionSummary?.maxBid,
          dealership_modal: confirmationMpTrackers?.outbidDealership,
          DGN_modal: confirmationMpTrackers?.outbidDealerGroup,
          is_proxy_updated: false,
          has_floor_price:
            !!tradeDetails?.hasFloorPrice ||
            !!tradeDetails?.details?.tradeFloorPrice?.hasFloorPrice ||
            !!liveBid?.hasFloorPrice,
          high_bid_modal: confirmationMpTrackers?.highBid,
          is_first_bid: isFirstBid(bidUsers, userId),
          floor_amount:
            tradeDetails?.details?.tradeFloorPrice?.valueDollars || null,
          fuel_type: tradeDetails?.details?.fuelType?.displayName,
          total_bids: numberOfBids,
          vehicle_launched_time: tradeDetails?.auctionSummary?.startTime
        }
      );
    }

    // Track mixpanel proxy bidding
    function trackProxyBid(userProxyAmount, userBidAmount) {
      if (!tradeDetails || !track) {
        return;
      }

      track(
        isOmp
          ? MixpanelConfig.names.placeBidOmp
          : MixpanelConfig.names.proxyBid,
        {
          ...defaultTrackingParameters,
          ...getSellerProperties(tradeDetails),
          bid_value: userBidAmount,
          current_top_bid: liveBid?.maxBidAmount,
          proxy_amount_value: userProxyAmount || null,
          total_bids: numberOfBids,
          is_proxy_updated: true,
          is_first_bid: isFirstBid(bidUsers, userId),
          is_floor_price:
            !!tradeDetails?.hasFloorPrice ||
            !!tradeDetails?.details?.tradeFloorPrice?.hasFloorPrice ||
            !!liveBid?.hasFloorPrice
        }
      );
    }

    // Track mixpanel bid error
    function trackBidError(errorMsg) {
      if (errorMsg?.length === 0 || !tradeDetails || !track) {
        return;
      }

      track(MixpanelConfig.names.bidError, {
        ...defaultTrackingParameters,
        ...getSellerProperties(tradeDetails),
        bid_error_type: errorMsg,
        is_first_bid: isFirstBid(bidUsers, userId)
      });
      // reset mixpanel error message
      setMpBidError([]);
    }

    // Track mixpanel bidding modal
    function trackBiddingModal(hasBid) {
      if (!tradeDetails || !track) {
        return;
      }

      track(MixpanelConfig.names.biddingModal, {
        trade_id: tradeDetails?.id,
        auction_id: tradeDetails?.auctionSummary?.id,
        is_first_bid: isFirstBid(bidUsers, userId),
        trade_state: TradeStateName[tradeState],
        source: bidSource,
        bid_applied: hasBid,
        buyer_dealer_id: biddingDealerId
      });
    }

    // If the mixpanel bid flags are truthy call the appropraite mixpanel
    // tracking function and resets the mixpanel bid flags
    if (trackBidFlag) {
      trackBid(bidAmount);
      setTrackBidFlag(false);
    }

    if (trackShowConfirmModal) {
      trackBiddingModal(trackHasBid);
      setTrackShowConfirmModal(false);
      setTrackHasBid(!trackHasBid);
    }

    if (trackProxyFlag) {
      trackProxyBid(proxyAmount, bidAmount);
      setTrackProxyFlag(false);
    }

    if (trackBidErrorFlag) {
      trackBidError(mpBidError);
      setTrackBidErrorFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    trackBidFlag,
    trackProxyFlag,
    trackBidErrorFlag,
    trackShowConfirmModal,
    trackHasBid,
    mpBidError,
    proxyAmount,
    numberOfBids,
    bidStatus,
    bidAmount,
    liveBid,
    tradeState
  ]);
};

export default useMpBidTracking;
